import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AnalyticsApiService from '../services/analyticsApi';
import Pagination from './ui/pagination';
import { toast } from 'sonner';
import ExpandedConversationModal from './ExpandedConversationModal';
import { transformBackendConversation } from '../utils/conversationTransform';
import { getPrimaryConversationUserId } from '../utils/conversationRecipients';
import { useAuth } from '../contexts/AuthContext';

interface FlagKind {
  flag_kind_id: number;
  name: string;
  description?: string;
}

interface UserFlag {
  flag_kind_id: number;
  flag_name: string;
  flag_description?: string;
  metadata?: any;
  created_at: string;
  updated_at?: string;
}

interface FlaggedUser {
  user_id: number;
  flags: UserFlag[];
  flag_count: number;
}

export function FlaggedUsersPage() {
  const { user } = useAuth();
  const [flaggedUsers, setFlaggedUsers] = useState<FlaggedUser[]>([]);
  const [flagKinds, setFlagKinds] = useState<FlagKind[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [selectedFlagFilter, setSelectedFlagFilter] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const itemsPerPage = 20;
  const [dateSort, setDateSort] = useState<'desc' | 'asc'>('desc');

  // Viewer state for showing linked conversation
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerConversation, setViewerConversation] = useState<any | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerFullHistory, setViewerFullHistory] = useState(false);

  useEffect(() => {
    loadFlagKinds();
  }, []);

  useEffect(() => {
    loadFlaggedUsers();
  }, [currentPage, selectedFlagFilter]);

  const loadFlagKinds = async () => {
    try {
      const response = await AnalyticsApiService.getFlagKinds();
      setFlagKinds(response.flag_kinds);
    } catch (error) {
      console.error('Error loading flag kinds:', error);
      toast.error('Failed to load flag types');
    }
  };

  const loadFlaggedUsers = async () => {
    setLoading(true);
    try {
      const response = await AnalyticsApiService.getAllFlaggedUsers(
        currentPage,
        itemsPerPage,
        selectedFlagFilter || undefined
      );
      
      setFlaggedUsers(response.items);
      setTotalPages(response.total_pages);
      setTotal(response.total);
    } catch (error) {
      console.error('Error loading flagged users:', error);
      toast.error('Failed to load flagged users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = flaggedUsers.filter(user => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      user.user_id.toString().includes(query) ||
      user.flags.some(flag => flag.flag_name.toLowerCase().includes(query))
    );
  });

  const getFlagBadgeColor = (flagName: string): string => {
    const colors: Record<string, string> = {
      'Bug': 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-700',
      'Good Chat': 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-700',
      'Power User': 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-300 dark:border-blue-700',
      'VIP': 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-700',
      'Type B': 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700',
    };
    return colors[flagName] || 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-400 border-gray-300 dark:border-gray-700';
  };

  const toggleRowExpansion = (userId: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const hasMetadata = (user: FlaggedUser): boolean => {
    return user.flags.some(flag => flag.metadata && (
      Array.isArray(flag.metadata) ? flag.metadata.length > 0 : Object.keys(flag.metadata).length > 0
    ));
  };

  const getLatestFlagTs = (user: FlaggedUser): number => {
    if (!user.flags || user.flags.length === 0) return 0;
    let maxTs = 0;
    for (const f of user.flags) {
      const t = new Date(f.created_at).getTime();
      if (!Number.isNaN(t) && t > maxTs) maxTs = t;
    }
    return maxTs;
  };

  const sortedUsers = useMemo(() => {
    const base = filteredUsers.slice();
    base.sort((a, b) => {
      const aTs = getLatestFlagTs(a);
      const bTs = getLatestFlagTs(b);
      if (dateSort === 'asc') return aTs - bTs;
      return bTs - aTs;
    });
    return base;
  }, [filteredUsers, dateSort]);

  const extractConversationIdFromFlag = (flag: UserFlag): number | null => {
    if (!flag.metadata) return null;
    const entries = Array.isArray(flag.metadata) ? flag.metadata : [flag.metadata];
    for (const entry of entries) {
      if (entry && (entry as any).conversation_id != null) {
        const cid = Number((entry as any).conversation_id);
        if (!Number.isNaN(cid)) return cid;
      }
    }
    return null;
  };

  const openLatestConversationForUser = useCallback(async (userId: number) => {
    try {
      setViewerLoading(true);
      const result = await AnalyticsApiService.getConversationsByUser(String(userId), { page: 1, limit: 1 });
      const latest = result.items?.[0];
      if (!latest) {
        toast.info('No conversations found for this user');
        return;
      }
      const transformed = transformBackendConversation(latest);
      setViewerConversation(transformed);
      setViewerOpen(true);
    } catch (e) {
      console.error('Failed to load latest conversation for user', e);
      toast.error('Failed to load conversation');
    } finally {
      setViewerLoading(false);
    }
  }, []);

  const openConversationViewer = useCallback(async (conversationId: number) => {
    try {
      setViewerLoading(true);
      const conv = await AnalyticsApiService.getConversation(conversationId, viewerFullHistory);
      const transformed = transformBackendConversation(conv);
      setViewerConversation(transformed);
      setViewerOpen(true);
    } catch (e) {
      console.error('Failed to load conversation', e);
      toast.error('Failed to load conversation');
    } finally {
      setViewerLoading(false);
    }
  }, [viewerFullHistory]);

  const handleFlagClick = useCallback((flag: UserFlag, vectordbUserId: number) => {
    const cid = extractConversationIdFromFlag(flag);
    if (cid) {
      openConversationViewer(cid);
    } else {
      openLatestConversationForUser(vectordbUserId);
    }
  }, [openConversationViewer, openLatestConversationForUser]);

  const handleViewerSendMessage = useCallback(async (conversationId: string, text: string) => {
    const optimisticId = `${conversationId}-${Date.now()}`;
    // Optimistic add
    setViewerConversation((prev: any) => {
      if (!prev) return prev;
      const now = new Date();
      const newMsg = {
        id: optimisticId,
        text,
        timestamp: now.toLocaleString(),
        isOwn: true,
        createdAt: now,
        deliveryStatus: 'sending',
      };
      return { ...prev, messages: [...(prev.messages || []), newMsg], lastMessageTime: now };
    });

    try {
      const recipientUserId = getPrimaryConversationUserId(viewerConversation);
      if (!recipientUserId) {
        throw new Error(`Unable to determine recipient user_id for conversation ${conversationId}`);
      }
      await AnalyticsApiService.sendAsAI(parseInt(conversationId, 10), text, recipientUserId);
      // Mark as sent
      setViewerConversation((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).map((m: any) =>
            m.id === optimisticId ? { ...m, deliveryStatus: 'sent', deliveryError: undefined } : m
          ),
        };
      });
    } catch (e) {
      console.error('Failed to send message', e);
      const errText = e instanceof Error ? e.message : String(e);
      setViewerConversation((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: (prev.messages || []).map((m: any) =>
            m.id === optimisticId ? { ...m, deliveryStatus: 'failed', deliveryError: errText } : m
          ),
        };
      });
      toast.error('Failed to send message');
    }
  }, [viewerConversation]);

  const handleViewerLiveMessage = useCallback((conversationId: string, messageData: any) => {
    const now = messageData.created_at ? new Date(messageData.created_at) : new Date();
    const msg = {
      id: messageData.id?.toString() || `live-${Date.now()}`,
      text: messageData.content || messageData.message || messageData.text || '',
      timestamp: now.toLocaleString(),
      isOwn: messageData.sender === 'ai_friend',
      createdAt: now,
      sender: messageData.sender,
      senderId: messageData.sender_id != null ? String(messageData.sender_id) : undefined,
      metadata: messageData.metadata_json ?? undefined,
    };
    setViewerConversation((prev: any) => {
      if (!prev) return prev;
      return { ...prev, messages: [...(prev.messages || []), msg], lastMessageTime: now };
    });
  }, []);

  const handleViewerToggleFullHistory = useCallback(async (conversationId: string, enabled: boolean) => {
    try {
      setViewerFullHistory(enabled);
      const conv = await AnalyticsApiService.getConversation(parseInt(conversationId, 10), enabled);
      const transformed = transformBackendConversation(conv);
      setViewerConversation(transformed);
    } catch (e) {
      console.error('Failed to toggle full history', e);
    }
  }, []);

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <div className="px-8 pt-6 pb-5 bg-gradient-to-b from-white/70 via-white/50 to-transparent dark:from-gray-900/70 dark:via-gray-900/50 dark:to-transparent backdrop-blur-xl sticky top-0 z-20">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-1">
              🏴 Flagged Users
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Manage user flags and classifications
            </p>
          </div>
          <button
            onClick={loadFlaggedUsers}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors"
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 items-center">
          {/* Search */}
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search by User ID or Flag Name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Flag Filter */}
          <select
            value={selectedFlagFilter || ''}
            onChange={(e) => {
              setSelectedFlagFilter(e.target.value ? parseInt(e.target.value) : null);
              setCurrentPage(1);
            }}
            className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Flags</option>
            {flagKinds.map(kind => (
              <option key={kind.flag_kind_id} value={kind.flag_kind_id}>
                {kind.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-8 py-8">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-gray-300 dark:border-gray-700 border-t-gray-600 dark:border-t-gray-400 rounded-full animate-spin"></div>
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl">🏴</span>
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg mb-2">
                  No Flagged Users
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchQuery ? 'No users match your search' : 'No users have been flagged yet'}
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100 w-12"></th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                        User ID
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Flags
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                        Flag Count
                      </th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">
                        <button
                          onClick={() => setDateSort(prev => prev === 'desc' ? 'asc' : 'desc')}
                          className="inline-flex items-center gap-1 hover:opacity-80"
                          title={dateSort === 'desc' ? 'Sort oldest first' : 'Sort newest first'}
                        >
                          Latest Flag
                          <span className="text-xs opacity-70">{dateSort === 'desc' ? '▼' : '▲'}</span>
                        </button>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {sortedUsers.map((user) => {
                      const latestFlag = user.flags.sort((a, b) => 
                        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                      )[0];
                      const isExpanded = expandedRows.has(user.user_id);
                      const userHasMetadata = hasMetadata(user);

                      return (
                        <React.Fragment key={user.user_id}>
                          <motion.tr
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                          >
                            <td className="px-6 py-4">
                              {userHasMetadata && (
                                <button
                                  onClick={() => toggleRowExpansion(user.user_id)}
                                  className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
                                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                >
                                  <svg
                                    className={`w-4 h-4 text-gray-600 dark:text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </button>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="font-mono text-sm text-gray-900 dark:text-gray-100">
                                {user.user_id}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                {user.flags.map((flag) => (
                                  <button
                                    key={`${flag.flag_kind_id}-${user.user_id}`}
                                    onClick={() => handleFlagClick(flag, user.user_id)}
                                    className={`px-2 py-1 rounded-full text-xs font-medium border ${getFlagBadgeColor(flag.flag_name)} hover:brightness-95 transition-colors`}
                                    title={extractConversationIdFromFlag(flag) ? 'View linked conversation' : 'View latest conversation'}
                                  >
                                    {flag.flag_name}
                                    {flag.metadata && (
                                      <span className="ml-1 text-[10px] opacity-60">
                                        ({Array.isArray(flag.metadata) ? flag.metadata.length : 1})
                                      </span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-900 dark:text-gray-100">
                                {user.flag_count}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-sm text-gray-600 dark:text-gray-400">
                                {latestFlag && new Date(latestFlag.created_at).toLocaleDateString()}
                              </div>
                            </td>
                          </motion.tr>
                          
                          {/* Expanded Metadata Row */}
                          <AnimatePresence>
                            {isExpanded && (
                              <motion.tr
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="bg-gray-50 dark:bg-gray-900/50"
                              >
                                <td colSpan={5} className="px-6 py-4">
                                  <div className="space-y-4">
                                    {user.flags.filter(flag => flag.metadata).map((flag) => (
                                      <div
                                        key={flag.flag_kind_id}
                                        className="bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 p-4"
                                      >
                                        <div className="flex items-start justify-between mb-3">
                                          <div className="flex items-center gap-2">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getFlagBadgeColor(flag.flag_name)}`}>
                                              {flag.flag_name}
                                            </span>
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              {Array.isArray(flag.metadata) ? flag.metadata.length : 1} entr{Array.isArray(flag.metadata) && flag.metadata.length !== 1 ? 'ies' : 'y'}
                                            </span>
                                          </div>
                                          {flag.updated_at && (
                                            <span className="text-xs text-gray-500 dark:text-gray-400">
                                              Updated: {new Date(flag.updated_at).toLocaleString()}
                                            </span>
                                          )}
                                        </div>
                                        
                                        <div className="space-y-3">
                                          {(Array.isArray(flag.metadata) ? flag.metadata : [flag.metadata]).map((entry: any, idx: number) => (
                                            <motion.div
                                              key={idx}
                                              initial={{ opacity: 0, y: -10 }}
                                              animate={{ opacity: 1, y: 0 }}
                                              transition={{ delay: idx * 0.05 }}
                                              className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                                            >
                                              {entry.timestamp && (
                                                <div className="flex items-center gap-2 mb-2 pb-2 border-b border-gray-300 dark:border-gray-600">
                                                  <svg className="w-3 h-3 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                  </svg>
                                                  <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                                                    {new Date(entry.timestamp).toLocaleString()}
                                                  </span>
                                                </div>
                                              )}
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {Object.entries(entry).filter(([key]) => key !== 'timestamp').map(([key, value]) => (
                                                  <div key={key} className="flex flex-col">
                                                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">
                                                      {key.replace(/([A-Z])/g, ' $1').trim()}
                                                    </span>
                                                    <span className="text-sm text-gray-900 dark:text-gray-100 break-words">
                                                      {String(value)}
                                                    </span>
                                                  </div>
                                                ))}
                                              </div>
                                              {entry && entry.conversation_id && (
                                                <div className="mt-3">
                                                  <button
                                                    onClick={() => openConversationViewer(Number(entry.conversation_id))}
                                                    className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                                                  >
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                    </svg>
                                                    View conversation
                                                  </button>
                                                </div>
                                              )}
                                            </motion.div>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </motion.tr>
                            )}
                          </AnimatePresence>
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    hasNext={currentPage < totalPages}
                    hasPrev={currentPage > 1}
                    total={total}
                    limit={itemsPerPage}
                    className=""
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {/* Conversation Viewer Modal - uses full-featured expanded modal to avoid layout issues */}
      {viewerConversation && (
        <ExpandedConversationModal
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          conversation={viewerConversation}
          onSendMessage={handleViewerSendMessage}
          onLiveMessage={handleViewerLiveMessage}
        />
      )}
    </div>
  );
}

// Inline modal viewer for conversations
// Renders the same chat UI component used in the Conversations page
// to ensure consistent look-and-feel
// Modal lives at the root of the page component
// (Place just before the final return closing tag earlier)



